// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
// custom
import Notify from "@bg/App/Components/Notify";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;

    tableWrapper?: InjectableCss;

    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataCell?: InjectableCss;
    contentWrapper?: InjectableCss;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
            margin-top: -10px;
        `,
    },
    cardList: {
        gridContainer: {
            css: css`
                a[data-test-selector="cardListHeadingViewAllLink"] {
                    margin-right: 10px;
                    margin-top: 5px;
                }
            `,
        },
    },
    contentWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
};

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
    customerSequence: "-1",
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;

        if (!canViewOrders) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper} data-test-selector="widget_RecentOrders">
                <CardListHeading heading={translate("Recent Orders")} viewAllUrl={orderHistoryUrl} />
                <StyledWrapper {...styles.contentWrapper} data-test-selector="recentOrders_NoOrdersFound">
                    {ordersDataView && ordersDataView.value && ordersDataView.value.length === 0 && (
                        <Notify
                            className="no-orders-found-text"
                            messageType="info"
                            body={<Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>}
                        />
                    )}
                    {ordersDataView && ordersDataView.value && ordersDataView.value.length !== 0 && (
                        <StyledWrapper {...styles.tableWrapper}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.dataTableHead}>
                                    <DataTableHeader tight title={translate("Order #")} {...styles.dataTableHeaders}>
                                        {translate("Date")}
                                    </DataTableHeader>
                                    <DataTableHeader tight {...styles.dataTableHeaders}>
                                        {translate("Order #")}
                                    </DataTableHeader>
                                    <DataTableHeader tight title={translate("Status")} {...styles.dataTableHeaders}>
                                        {translate("Status")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        tight
                                        title={translate("Web Order #")}
                                        {...styles.dataTableHeaders}
                                    >
                                        {translate("Web Order #")}
                                    </DataTableHeader>
                                    <DataTableHeader tight title={translate("Total")} {...styles.dataTableHeaders}>
                                        {translate("Total")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {(ordersDataView.value || []).map(
                                        order =>
                                            order !== null && (
                                                <DataTableRow
                                                    {...styles.dataTableRow}
                                                    data-test-selector="recentOrders_dataLine"
                                                    key={order.id}
                                                >
                                                    <OrderSummaryCard
                                                        order={order}
                                                        orderSettings={settingsCollection.orderSettings}
                                                        extendedStyles={styles.orderSummaryCard}
                                                    />
                                                </DataTableRow>
                                            ),
                                    )}
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "XNGAGE" as any,
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
